/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { supabase } from '../utils/supabaseClient';
import { useAuth } from '../utils/AuthContext';
import { toast } from 'react-toastify';
import { History, Download, Trash2 } from 'lucide-react';

const PaymentDetails = () => {
    const { user } = useAuth();
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(false); // Default to false, wait for user to click Show

    // Filters
    const [customerId, setCustomerId] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [hasSearched, setHasSearched] = useState(false);

    // Autocomplete State
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);

    // Fetch customer suggestions
    const fetchSuggestions = async (text) => {
        if (!text || text.length < 1) {
            setSuggestions([]);
            return;
        }

        try {
            const { data, error } = await supabase
                .from('customers')
                .select('customer_name, customer_id')
                .ilike('customer_name', `${text}%`) // Starting with...
                .limit(5);

            if (error) throw error;
            setSuggestions(data || []);
            setShowSuggestions(true);
        } catch (error) {
            console.error('Error fetching suggestions:', error);
        }
    };

    const handleCustomerChange = (e) => {
        const value = e.target.value;
        setCustomerId(value);
        fetchSuggestions(value);
    };

    const selectSuggestion = (name) => {
        setCustomerId(name);
        setSuggestions([]);
        setShowSuggestions(false);
    };

    // Close suggestions on click outside
    useEffect(() => {
        const handleClickOutside = () => setShowSuggestions(false);
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    // Fetch payments based on filters
    const handleShow = async () => {
        setLoading(true);
        setHasSearched(true);
        try {
            let query = supabase
                .from('payments')
                .select('*, invoices!inner(invoice_no, invoice_date, customer_id, customers(customer_name))') // Inner join to filter by invoice props if needed
                .eq('user_id', user.id)
                .order('payment_date', { ascending: false });

            // Apply Date Filter
            if (startDate) {
                query = query.gte('payment_date', startDate);
            }
            if (endDate) {
                query = query.lte('payment_date', endDate);
            }

            const { data, error } = await query;
            if (error) throw error;

            // Client-side filter for Customer ID/Name if strictly needed, 
            // or we could try to filter via Supabase if we knew the exact column structure.
            // asking for "Customer ID" input usually implies searching by that ID or Name.
            // Since we joined customers, we can filter data here.

            let filteredData = data || [];

            if (customerId) {
                const searchLower = customerId.toLowerCase();
                filteredData = filteredData.filter(p => {
                    const custName = p.invoices?.customers?.customer_name?.toLowerCase() || '';
                    // Allow exact match or partial if typed manually
                    return custName.includes(searchLower);
                });
            }

            setPayments(filteredData);
        } catch (error) {
            console.error('Error fetching payments:', error);
            toast.error('Failed to load payment history');
        } finally {
            setLoading(false);
        }
    };

    const handleDeletePayment = async (paymentId, invoiceId) => {
        if (!window.confirm('Are you sure you want to delete this payment record? The deleted record amount will be added back to the invoice balance.')) return;
        try {
            // Delete from payments
            const { error: deleteError } = await supabase.from('payments').delete().eq('id', paymentId);
            if (deleteError) throw deleteError;

            // Recalculate invoice totals
            const { data: remainingPayments, error: historyError } = await supabase
                .from('payments')
                .select('amount, tds_amount')
                .eq('invoice_id', invoiceId);
            if (historyError) throw historyError;

            const newTotalPaid = remainingPayments.reduce((sum, p) => sum + parseFloat(p.amount) + parseFloat(p.tds_amount || 0), 0);
            const newTotalTDS = remainingPayments.reduce((sum, p) => sum + parseFloat(p.tds_amount || 0), 0);

            // Need the grand_total to determine new status
            const { data: invoiceData, error: invoiceFetchError } = await supabase
                .from('invoices')
                .select('grand_total')
                .eq('id', invoiceId)
                .single();
            
            if (invoiceFetchError) throw invoiceFetchError;
            
            const grandTotal = parseFloat(invoiceData.grand_total || 0);

            let newStatus = 'PENDING';
            if (newTotalPaid >= grandTotal - 1 && newTotalPaid > 0) {
                newStatus = 'PAID';
            } else if (newTotalPaid > 0) {
                newStatus = 'PARTIAL';
            }

            const { error: updateError } = await supabase
                .from('invoices')
                .update({
                    amount_paid: newTotalPaid,
                    tds_amount: newTotalTDS,
                    payment_status: newStatus
                })
                .eq('id', invoiceId);

            if (updateError) throw updateError;

            toast.success('Payment deleted successfully');
            // Remove from local list to update UI instantly without reloading all bounds
            setPayments(payments.filter(p => p.id !== paymentId));

        } catch (error) {
            console.error('Error deleting payment:', error);
            toast.error('Failed to delete payment');
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                        <History className="mr-2 h-6 w-6 text-primary-600" />
                        Payment Details
                    </h1>
                    <p className="text-gray-600">View detailed history of all transactions</p>
                </div>
            </div>

            {/* Filters Section */}
            <div className="bg-white p-6 rounded-lg shadow space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                    <div className="relative" onClick={(e) => e.stopPropagation()}>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Customer Name
                        </label>
                        <input
                            type="text"
                            value={customerId}
                            onChange={handleCustomerChange}
                            onFocus={() => customerId && fetchSuggestions(customerId)}
                            placeholder="Enter Customer Name"
                            className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                            autoComplete="off"
                        />
                        {showSuggestions && suggestions.length > 0 && (
                            <ul className="absolute z-10 w-full bg-white border border-gray-300 rounded-md mt-1 shadow-lg max-h-60 overflow-y-auto">
                                {suggestions.map((customer, idx) => (
                                    <li
                                        key={idx}
                                        onClick={() => selectSuggestion(customer.customer_name)}
                                        className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm text-gray-700"
                                    >
                                        {customer.customer_name}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            From Date
                        </label>
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            To Date
                        </label>
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                        />
                    </div>
                    <div>
                        <button
                            onClick={handleShow}
                            disabled={loading}
                            className="w-full flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:bg-gray-400"
                        >
                            {loading ? 'Loading...' : 'Show Details'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Results Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sr. No</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Inv Number</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Inv Date</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment Date</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer ID</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mode of Payment</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reference</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">TDS</th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {loading ? (
                            <tr>
                                <td colSpan="10" className="px-6 py-8 text-center text-gray-500">
                                    Loading...
                                </td>
                            </tr>
                        ) : payments.length > 0 ? (
                            payments.map((payment, index) => (
                                <tr key={payment.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {index + 1}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                        {payment.invoices?.invoice_no}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {payment.invoices?.invoice_date}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {payment.payment_date}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {/* Displaying Customer Name as ID/Name combo or just ID? 
                                            Request said "Customer ID". 
                                            Usually ID is UUID which is ugly, but maybe they mean 'Customer Code' if exists.
                                            Let's show Name for readability unless strict ID is needed. 
                                            But user said "Customer ID", let's check if 'customer_id' is meaningful. 
                                            It's likely the foreign key. I'll show Name (ID) for useful context.
                                        */}
                                        {payment.invoices?.customer_id && (
                                            <span title={payment.invoices?.customers?.customer_name}>
                                                {payment.invoices?.customers?.customer_name}
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                        {payment.payment_mode}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {payment.reference_number || '-'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600 text-right">
                                        ₹{parseFloat(payment.amount).toLocaleString()}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-600 text-right">
                                        ₹{parseFloat(payment.tds_amount || 0).toLocaleString()}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm">
                                        <button
                                            onClick={() => handleDeletePayment(payment.id, payment.invoice_id)}
                                            className="text-red-400 hover:text-red-600 transition-colors"
                                            title="Delete payment"
                                        >
                                            <Trash2 className="w-5 h-5 mx-auto" />
                                        </button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="10" className="px-6 py-8 text-center text-gray-500">
                                    {hasSearched ? 'No transaction history found for the selected filters.' : 'Enter filters and click Show Details.'}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default PaymentDetails;
